/**
 * HOUSE SKINS — cosmetic variants for the greenhouse SHELL (glazing + frame + outer wall posts) and its
 * FLOOR, selected by id from the save (`selectedBuildingSkin` / `selectedFloorSkin`, see sim/save.ts) and
 * drawn by render/pixi/RackLayer's buildInterior().
 *
 * THIS FILE IS THE DATA HALF — ids, names, descriptions, the reward flag, the palettes, the defaults and
 * the load-bearing geometry (HouseGeom, roofCorner, FLOOR_RISE/FLOOR_DEPTH). The DRAWERS live in
 * render/pixi/houseSkinArt.ts. They were split because sim/ never imports render/pixi: collection.ts and
 * cosmeticNames.ts read skin names from here, and when the drawers lived in this file that one import
 * pulled Pixi into the landing page's entry chunk (986 KB vs 479 KB). sim/simPurity.test.ts enforces it.
 * A new skin is an entry here AND its drawers there; sim/skinArt.test.ts fails if the two drift.
 *
 * Modelled on sim/potSkins.ts, which solved the same problem for pots: a palette plus a drawer behind a
 * HARD GEOMETRIC CONTRACT, with the default skin reproducing the original pixel-for-pixel so an unknown or
 * absent id is always safe.
 *
 * ── THE CONTRACT ────────────────────────────────────────────────────────────────────────────────────────
 *
 * A SKIN OWNS: colour, material and surface detail. The frame palette, the glass tint and its opacity, the
 * mullion/transom rhythm, the roof lattice pattern, the tile pattern and slab palette, trim, dressing.
 *
 * A SKIN DOES NOT OWN THE LOAD-BEARING GEOMETRY. Everything in `HouseGeom` is the LAYOUT's, not the skin's:
 *
 *   • `xL` / `xR` — the interior span (layout.floorLeft → totalWidth+40). The floor clip, the rain
 *     occlusion test and the scene's grass masking all read this span.
 *   • `eaveY` / `ridgeY` — the wall↔roof junction and the roof peak. RackScene reserves fit-headroom from
 *     `greenhouseTopY()` = ridgeY; rain occlusion tests against both.
 *   • `groundY` — the shelf-base line. The racks stand on it, pots are placed against it, the composter and
 *     robot dock sit on it.
 *   • `corner` — the roof's rounded-corner radius. **THIS IS THE TRAP.** RackLayer's rain collision
 *     (`roofCurve`) re-derives the exact quadratic the roof is drawn with to decide whether a drop is
 *     inside the house. A skin that changed the roof silhouette would silently desync rain occlusion, so
 *     the radius comes from `roofCorner()` below — the ONE place that formula lives — and a skin receives
 *     it as a number it may decorate but must not redefine. Draw your glazing inside the silhouette; do
 *     not invent a different one.
 *   • `floorTopY` / `floorDepth` / `floorRise` — the floor plane. The rack post pools and the front sill
 *     are pinned to these; the front sill in particular is what the HUD crops against.
 *
 * In short: a skin repaints the building, it does not move it. If a future skin genuinely wants a different
 * roof profile, the curve must move into `roofCorner()`/`roofY()` FIRST so the rain follows it — changing
 * it inside a drawer is the one edit that breaks the scene without looking like it did.
 *
 * The drawers' BATCHING rule (one Graphics each, never create display objects) is in houseSkinArt.ts.
 *
 * ── THE DEFAULT ─────────────────────────────────────────────────────────────────────────────────────────
 *
 * "glasshouse" and "stone" are the fallback for an unknown or absent id (buildingSkin / floorSkin below).
 */

/**
 * THE LOAD-BEARING GEOMETRY, owned by the layout and handed to every skin read-only. See the contract in
 * the file header: a skin draws INSIDE these numbers and never redefines one.
 */
export interface HouseGeom {
  /** Interior span, world x (layout.floorLeft → layout.totalWidth + 40). */
  xL: number;
  xR: number;
  /** xR − xL, precomputed because every drawer wants it. */
  W: number;
  /** Wall↔roof junction, world y. */
  eaveY: number;
  /** Roof peak, world y (= layout.ridgeY). */
  ridgeY: number;
  /** Shelf-base line, world y — what the racks, pots, dock and composter stand on. */
  groundY: number;
  /** groundY − eaveY. */
  wallH: number;
  /** eaveY − ridgeY. */
  roofH: number;
  /** THE ROOF CORNER RADIUS. Comes from roofCorner(); the rain collision re-derives the same curve. */
  corner: number;
  /** How far the floor plane extends UP behind the shelves (groundY − floorTopY). */
  floorRise: number;
  /** Top of the floor plane, world y. */
  floorTopY: number;
  /** How far the floor extends DOWN from floorTopY. */
  floorDepth: number;
  /** Rack run geometry, for grounding the posts: count, pitch and width. */
  rackCount: number;
  rackWidth: number;
  rackGap: number;
}

/**
 * THE ROOF CORNER RADIUS — the one place this formula lives.
 *
 * The roof's two top corners are quarter-round quadratics of this radius, and RackLayer's rain collision
 * solves that same Bézier (`y = ridgeY + (1 − √(dx/corner))²·corner`) to decide whether a drop is inside
 * the house. Drawing and collision MUST agree, so both call this. Do not inline the expression anywhere.
 */
export function roofCorner(roofH: number, W: number): number {
  return Math.min(90, roofH * 0.9, W * 0.16);
}

/** How far the floor plane extends UP behind the shelves — layout-owned (the shelves visibly stand ON it). */
export const FLOOR_RISE = 70;
/** How far the floor plane extends DOWN from its top edge — layout-owned (the front sill is pinned to it). */
export const FLOOR_DEPTH = 170;

// ── BUILDING SKINS ────────────────────────────────────────────────────────────────────────────────────

/** The recolourable tones of the shell's METAL. `frame` is the structural body, `hi` the one lit arris that
 *  models it as an extrusion, `dk` the shadow side; `outer*` are the two full-height framing posts. */
export interface BuildingPalette {
  frame: number;
  frameHi: number;
  frameDk: number;
  outer: number;
  outerHi: number;
  outerSh: number;
}

/** A shell skin's DATA. Its drawers (glazing, outer wall) are in render/pixi/houseSkinArt.ts. */
export interface BuildingSkin {
  id: string;
  name: string;
  desc: string;          // one-liner for the picker
  /** A REWARD skin: locked until an achievement unlocks it (sim/cosmetics.ts). The original four are free. */
  reward?: boolean;
  palette: BuildingPalette;
}

export const BUILDING_SKINS: BuildingSkin[] = [
  {
    id: "glasshouse",
    name: "Glasshouse",
    desc: "The original tinted glass on a near-black steel frame. Free for everyone.",
    palette: {
      frame: 0x12161a, frameHi: 0x5a6470, frameDk: 0x080b0e,   // near-black structural metal
      outer: 0x0c1013, outerHi: 0x4a545e, outerSh: 0x05070a,
    },
  },
  {
    id: "victorian",
    name: "Victorian Conservatory",
    desc: "Thin painted ironwork with a hundred small panes and an ornate roof ridge.",
    palette: {
      // OFF-WHITE, NEVER WHITE (§2.2), AND NOT EVEN THE IVORY. The first pass authored this at 0xd8dcd0
      // (value 0.86) with an 0xf0ede0 arris, and the conservatory came out as the single brightest object
      // in the game — a white cage in front of a dark wood, exactly the "brighter than any entry in the
      // palette" failure the guide records against the stone decor. WHITE PAINT IN A DARK SCENE IS A
      // MID-VALUE GREY-GREEN: 0x9aa89c sits at value 0.66, still clearly the palest frame of the four and
      // still obviously *painted*, without becoming the light source. The ivory survives only as the lit
      // arris, at a third of its old alpha. **Do not lift `frame` back toward the ivory** — and note that
      // it reads BRIGHTER on screen than its hex suggests, because the scene's own sun/light pass
      // (sunLightG) runs over the glazing afterwards. Author it low and let the light lift it.
      frame: 0x7b8c80, frameHi: 0xcdd4c4, frameDk: 0x141a1a,
      // The framing posts keep the glasshouse's near-black body — a pale post at the picture's edge would
      // be the brightest thing on screen. The paint shows as the flutes and the capital only.
      outer: 0x10151a, outerHi: 0xcdd2c8, outerSh: 0x05080b,
    },
  },
  {
    // THE KICKSTARTER BACKER SHELL: the Victorian conservatory, drawn by its own drawers, in black-nickel
    // ironwork with every lit arris and the cresting picked out in gilt. These are the exact values the
    // backer mockup was rendered with (cosmetic-rewards-assets render.mjs NICKEL_SHELL over victorian) —
    // backers saw this picture, so the shared geometry is intended and the gold is NOT to be toned down.
    id: "foundersConservatory",
    name: "Founders' Conservatory",
    desc: "The whole greenhouse in black iron with gold trim.",
    reward: true,
    palette: {
      frame: 0x2a2d32, frameHi: 0xf0c860, frameDk: 0x07080a,
      outer: 0x14161a, outerHi: 0xe0b450, outerSh: 0x040506,
    },
  },
  {
    id: "copper",
    name: "Weathered Copper",
    desc: "A heavy copper frame, turned green at the joints.",
    palette: {
      // The first pass authored the body at 0x2f4438 — almost exactly the backdrop tree green — and the
      // whole shell vanished into the wood behind it: a copper house you could not see was copper. The
      // body is now a WARM DARK BROWN-GREEN (0x3a3a2c, the palette's mud), which is what oxidised copper
      // in shadow actually is, and which no longer matches anything in the backdrop. Separation comes from
      // hue as much as value, so do not simply lighten this if the frame goes quiet again.
      // frameHi is BARE metal, not a lighter green — that warm arris is the whole material read.
      frame: 0x3a3a2c, frameHi: 0xd08a52, frameDk: 0x0d100c,
      outer: 0x15150f, outerHi: 0xa06a44, outerSh: 0x060806,
    },
  },
  {
    id: "obsidian",
    name: "Obsidian Steel",
    desc: "A minimal matte-black frame with very large panes of cold, reflective glass.",
    palette: {
      // Matte powder-coat: the deepest members in the set, and deliberately given almost no highlight —
      // see obsidianGlazing's hiA of 0.3. A matte surface does not catch an arris.
      frame: 0x0e1114, frameHi: 0x7e94a4, frameDk: 0x030507,
      outer: 0x070a0d, outerHi: 0x6a8090, outerSh: 0x020305,
    },
  },
  // ── the five REWARD shells (locked until earned — sim/cosmetics.ts) ──
  {
    id: "summerhouse",
    name: "Summerhouse",
    desc: "Pale blue painted wood with old-fashioned windows and scalloped trim.",
    reward: true,
    palette: {
      // Duck-egg authored LOW (value ~0.56) for the Victorian's reason — the sun pass lifts it after. The
      // frameDk is a deep teal-black so the chunky members still carry their shadow-side ink.
      frame: 0x668a84, frameHi: 0xb2cbc4, frameDk: 0x111a19,
      outer: 0x172120, outerHi: 0x8fb0aa, outerSh: 0x060a0a,
    },
  },
  {
    id: "palmHouse",
    name: "Palm House",
    desc: "Thin wrought iron and curved ribs, with condensation on the glass.",
    reward: true,
    palette: {
      // Painted wrought iron: a green-black body, clearly darker than the Victorian's paint and greener
      // than the glasshouse's steel, so three iron frames never read as one.
      frame: 0x2c3a35, frameHi: 0x86a095, frameDk: 0x070c0a,
      outer: 0x0d1311, outerHi: 0x5d7068, outerSh: 0x040706,
    },
  },
  {
    id: "moonlit",
    name: "Moonlit Conservatory",
    desc: "Near-black iron arches with faintly green glass that seems to hold a little light.",
    reward: true,
    palette: {
      // The darkest iron after Obsidian's. frameHi is a dim green-grey rather than a steel grey — the
      // real "lit edge" on this skin is the glow-green rim the drawers add, and a grey arris beside it
      // would read as a second light.
      frame: 0x101614, frameHi: 0x5f7a70, frameDk: 0x030605,
      outer: 0x080c0b, outerHi: 0x4f6a60, outerSh: 0x020403,
    },
  },
  {
    id: "cedarWorkshop",
    name: "Cedar Workshop",
    desc: "Thick weathered cedar posts and whitewashed roof glass, like a potting shed.",
    reward: true,
    palette: {
      // Warm red-brown cedar in shade — the second warm frame after Copper, separated from it by hue
      // (red-brown against copper's brown-green) and by the silvering, which is grey where copper's
      // weathering is green. frameHi is the fresh-cut cedar the arris still shows.
      frame: 0x433328, frameHi: 0xa47a58, frameDk: 0x120b07,
      outer: 0x1c130d, outerHi: 0x8b877a, outerSh: 0x070403,
    },
  },
  {
    id: "crystalConservatory",
    name: "Crystal Conservatory",
    desc: "Pale steel and a diamond lattice of faceted glass that catches the light.",
    reward: true,
    palette: {
      // Pale steel, cooler and bluer than the Victorian's grey-green paint; see crystalGlazing's header on
      // why the arris is held down. The posts at the picture's edge stay dark steel.
      frame: 0x8aa6a6, frameHi: 0xd6e6de, frameDk: 0x162020,
      outer: 0x0e1415, outerHi: 0xa9c4c2, outerSh: 0x040708,
    },
  },
];

// ── FLOOR SKINS ───────────────────────────────────────────────────────────────────────────────────────

/** The recolourable tones of the floor's SURFACE. `ink` is the black BETWEEN the slabs and is also mixed
 *  into the back-edge shadow, so it should stay in the same hue family as the slabs (see the note on
 *  keeping chroma in the dark). */
export interface FloorPalette {
  stone: number;
  stoneDk: number;
  stoneLt: number;
  moss: number;
  ink: number;
}

/** A floor skin's DATA. Its surface drawer is in render/pixi/houseSkinArt.ts. */
export interface FloorSkin {
  id: string;
  name: string;
  /** A REWARD skin: locked until an achievement unlocks it (sim/cosmetics.ts). The original four are free. */
  reward?: boolean;
  desc: string;
  palette: FloorPalette;
}

export const FLOOR_SKINS: FloorSkin[] = [
  {
    id: "stone",
    name: "Dressed Stone",
    desc: "Cracked stone slabs with a cold green tint. Free for everyone.",
    palette: { stone: 0x5c7a68, stoneDk: 0x3f5a4c, stoneLt: 0x74967f, moss: 0x4e7247, ink: 0x111f1a },
  },
  {
    id: "encaustic",
    name: "Victorian Encaustic",
    desc: "Patterned Victorian tile in ochre and red-brown.",
    palette: {
      // The WARMEST palette in the set, and the only one that is not green-cast — encaustic pigment is
      // earth, and the contrast against a cold teal-green house is the whole point of the skin. `stone`
      // and `stoneLt` are the ground and its polish; the ochre/red figure lives in the baked tile, because
      // a patterned floor's colours belong to the pattern.
      // `stoneLt` is the ceiling here and it is a MID VALUE (0.62), not an off-white: the scene's sun pass
      // runs over this floor afterwards and lifts it. Authored at the ivory it became the brightest object
      // in the greenhouse — brighter than the glazing lighting it. **Do not lift it toward #f0ede0.**
      stone: 0x8a8272, stoneDk: 0x5e5849, stoneLt: 0x9e9887, moss: 0x5a6340, ink: 0x140f0c,
    },
  },
  {
    id: "flagstone",
    name: "Mossy Flagstone",
    desc: "Uneven gray-green slabs with moss in the joints.",
    palette: {
      // Cooler and greyer than the dressed stone it sits beside — the two would otherwise read as the same
      // floor, and `stone` is the one they are most likely to be compared against. Separation is by
      // CHROMA: these slabs are nearly neutral so the moss on them is the only saturated thing on the
      // floor, where the dressed stone's slabs carry the green themselves.
      stone: 0x55655c, stoneDk: 0x3e4d46, stoneLt: 0x6d7d70, moss: 0x44603c, ink: 0x0d1512,
    },
  },
  {
    id: "concrete",
    name: "Polished Concrete",
    desc: "Smooth cool gray concrete with a soft sheen.",
    palette: {
      // The coldest and most neutral of the four, and deliberately the flattest: this material has almost
      // no local colour, so everything it shows comes from the light. `stoneLt` is the polish, not a
      // highlight — the actual sheen is laid by the surface drawer where the sun shafts land, and it is
      // near-white so the multiply darken can kill it at night. See concreteSurface's header.
      stone: 0x4a5458, stoneDk: 0x394245, stoneLt: 0x5c686c, moss: 0x3f5a46, ink: 0x10161a,
    },
  },
  // ── the five REWARD floors (locked until earned — sim/cosmetics.ts). The palettes mirror their tiles'
  //    own tones (floorTile.ts) so the picker swatch and the floor dressing agree with the pattern. ──
  {
    id: "herringbone",
    name: "Herringbone Brick",
    reward: true,
    desc: "Dark red brick laid in a herringbone pattern.",
    palette: { stone: 0x5a2f24, stoneDk: 0x4a261d, stoneLt: 0x683a2c, moss: 0x3d5a3a, ink: 0x140c09 },
  },
  {
    id: "terrazzo",
    name: "Dark Terrazzo",
    reward: true,
    desc: "Charcoal terrazzo with stone chips and dull brass strips.",
    // stoneLt is the swatch's arris — a mid grey-green, NOT the marble chip tone
    palette: { stone: 0x283030, stoneDk: 0x1f2626, stoneLt: 0x4a5451, moss: 0x3d5a46, ink: 0x0e1312 },
  },
  {
    id: "slateMoss",
    name: "Slate & Moss",
    reward: true,
    desc: "Uneven blue-black slate with moss in the seams.",
    palette: { stone: 0x333d3f, stoneDk: 0x2a3336, stoneLt: 0x4d5857, moss: 0x3a5534, ink: 0x0b1110 },
  },
  {
    id: "cedarDeck",
    name: "Cedar Decking",
    reward: true,
    desc: "Oiled cedar boards with long grain and knots.",
    palette: { stone: 0x5a3825, stoneDk: 0x4a2c1c, stoneLt: 0x6c4a33, moss: 0x3d5a3a, ink: 0x120a06 },
  },
  {
    id: "hexEncaustic",
    name: "Hexagon Encaustic",
    reward: true,
    desc: "Dusky rose hexagon tiles with teal inlays.",
    // stoneDk carries the TEAL so the picker swatch shows the two-tone; the pattern itself is in the tile
    palette: { stone: 0x57443e, stoneDk: 0x2d4845, stoneLt: 0x6e5a52, moss: 0x3d5a3a, ink: 0x121513 },
  },
];

// ── LOOKUP ────────────────────────────────────────────────────────────────────────────────────────────

const BUILDING_BY_ID = new Map(BUILDING_SKINS.map((s) => [s.id, s]));
const FLOOR_BY_ID = new Map(FLOOR_SKINS.map((s) => [s.id, s]));

/** The default shell skin id (the original glasshouse) — used when none is selected/unlocked. */
export const DEFAULT_BUILDING_SKIN = "glasshouse";
/** The default floor skin id (the original dressed stone) — used when none is selected/unlocked. */
export const DEFAULT_FLOOR_SKIN = "stone";

/** Resolve a shell skin id, falling back to the default for unknown/absent ids. */
export function buildingSkin(id: string | undefined): BuildingSkin {
  return (id && BUILDING_BY_ID.get(id)) || BUILDING_BY_ID.get(DEFAULT_BUILDING_SKIN)!;
}

/** Resolve a floor skin id, falling back to the default for unknown/absent ids. */
export function floorSkin(id: string | undefined): FloorSkin {
  return (id && FLOOR_BY_ID.get(id)) || FLOOR_BY_ID.get(DEFAULT_FLOOR_SKIN)!;
}

// THE UNLOCK GATE lives in sim/cosmetics.ts (skinOpen): a skin flagged `reward` is locked until earned.
